/*
 * Tabbar: tabs with one content panel each.
 *
 *   const tabs = new dfc.Tabbar('#box', {
 *     tabs: [
 *       { id: 'details', label: 'Details', icon: 'document' },
 *       { id: 'files', label: 'Files', count: 3 },
 *       { id: 'notes', label: 'Notes', closable: true },
 *     ],
 *     active: 'details',
 *   });
 *   tabs.attach('details', formElement);       // or an HTML string
 *   tabs.getPanel('files').appendChild(list);  // the panel is a plain element
 *   tabs.on('change', ({ id, previous }) => { ... });
 *
 * Panels stay in the page while hidden, so whatever is mounted in a tab keeps
 * its state when the user switches away and back.
 *
 * A tab: { id, label, icon, closable, disabled, hidden, count, countColor, css, tooltip }.
 * `tab` is accepted for `label`, and `views` for `tabs`.
 * Keyboard (WAI-ARIA tabs): arrows move between tabs and select them (Up and
 * Down when the tabs run vertically), Home and End jump, Delete closes a
 * closable tab. With `activation: 'manual'`, arrows only move focus and Enter
 * or Space selects.
 */

import { Component, h, register } from './core.js';
import { renderIcon } from './icons.js';
import { EventChannel } from './events.js';

function key(id) { return String(id); }

export class Tabbar extends Component {
	static get componentName() { return 'tabbar'; }
	static get detachable() { return true; }

	defaults() {
		return {
			/** The tabs. `views` is accepted as another name for it. */
			tabs: null,
			/** Id of the tab open at start (`activeTab` also accepted); defaults to the first. */
			active: null,
			/** Where the tabs sit: 'top', 'bottom', 'left' or 'right'. */
			mode: 'top',
			/** Alignment of the tabs along their edge: 'start', 'center' or 'end' ('left'/'right' accepted). */
			tabAlign: 'start',
			/** Header only, with no panels: the host swaps its own content on change. */
			noContent: false,
			/** Every tab gets a close button (a tab's own `closable` overrides). */
			closable: false,
			/** 'auto' selects on arrow keys; 'manual' needs Enter or Space. */
			activation: 'auto',
			/** Extra classes for the root (in addition to className). */
			css: '',
			/** Accessible name of the tab list. */
			ariaLabel: '',
		};
	}

	// ── Setup and rendering ────────────────────────────────────────────────

	_init() {
		this._ready = true;
		this._tabs = [];
		this._byId = new Map();
		this._panels = new Map();
		this._appliedCss = [];
		this.events = new EventChannel(this, (name, args) => this._relay(name, args));
		const given = this.options.tabs || this.options.views || [];
		this._from = given;
		given.forEach((t) => this._store(t));
		const want = this.options.active !== null && this.options.active !== undefined ? this.options.active : this.options.activeTab;
		const first = this._tabs.find((t) => !t.hidden && !t.disabled);
		this._active = want !== null && want !== undefined && this._byId.has(key(want)) ? key(want) : (first ? key(first.id) : null);
	}

	_store(raw, index) {
		const tab = Object.assign({}, raw);
		if (tab.label === undefined && tab.tab !== undefined) tab.label = tab.tab;
		if (tab.id === undefined || tab.id === null) tab.id = 'tab' + (this._tabs.length + 1);
		if (this._byId.has(key(tab.id))) { console.warn('dfc-tabbar: duplicate tab id "' + tab.id + '"'); return null; }
		this._byId.set(key(tab.id), tab);
		if (index === undefined || index === null || index < 0 || index > this._tabs.length) this._tabs.push(tab);
		else this._tabs.splice(index, 0, tab);
		return tab;
	}

	render() {
		if (!this._ready) this._init();
		const given = this.options.tabs || this.options.views || null;
		if (given && given !== this._from) {
			this._from = given;
			const keep = new Set(given.map((t) => key(t.id)));
			[...this._panels.keys()].forEach((id) => { if (!keep.has(id)) { this._panels.get(id).remove(); this._panels.delete(id); } });
			this._tabs = []; this._byId.clear();
			given.forEach((t) => this._store(t));
			if (!this._byId.has(this._active)) { const f = this._tabs.find((t) => !t.hidden && !t.disabled); this._active = f ? key(f.id) : null; }
		}
		const el = this.el;
		const mode = ['top', 'bottom', 'left', 'right'].includes(this.options.mode) ? this.options.mode : 'top';
		el.className = el.className.split(/\s+/).filter((c) => !/^dfc-tabbar--/.test(c)).join(' ');
		this._appliedCss.forEach((c) => el.classList.remove(c));
		this._appliedCss = String(this.options.css || '').split(/\s+/).filter(Boolean);
		this._appliedCss.forEach((c) => el.classList.add(c));
		el.classList.add('dfc-tabbar--' + mode);
		const align = { left: 'start', right: 'end' }[this.options.tabAlign] || this.options.tabAlign || 'start';
		el.classList.add('dfc-tabbar--align-' + align);
		if (this.options.noContent) el.classList.add('dfc-tabbar--no-content');
		this._vertical = mode === 'left' || mode === 'right';

		if (!this._header) {
			this._header = h('div', { className: 'dfc-tabbar__header' });
			this._list = h('div', { className: 'dfc-tabbar__list', role: 'tablist' });
			this._header.appendChild(this._list);
			this._body = h('div', { className: 'dfc-tabbar__panels' });
			el.appendChild(this._header);
			el.appendChild(this._body);
		}
		this._list.setAttribute('aria-orientation', this._vertical ? 'vertical' : 'horizontal');
		if (this.options.ariaLabel) this._list.setAttribute('aria-label', this.options.ariaLabel); else this._list.removeAttribute('aria-label');
		this._body.hidden = !!this.options.noContent;
		this._renderTabs();
	}

	_renderTabs() {
		const focused = this._list.contains(document.activeElement) ? document.activeElement.dataset.id : null;
		this._list.innerHTML = '';
		for (const tab of this._tabs) this._list.appendChild(this._buildTab(tab));
		// Panels: one per tab, kept across renders and reordered to match.
		if (!this.options.noContent) {
			for (const tab of this._tabs) {
				let p = this._panels.get(key(tab.id));
				if (!p) {
					p = h('div', { className: 'dfc-tabbar__panel', role: 'tabpanel', tabIndex: 0 });
					p.id = this.id + '-panel-' + this._safe(tab.id);
					p.dataset.id = key(tab.id);
					this._panels.set(key(tab.id), p);
				}
				p.setAttribute('aria-labelledby', this.id + '-tab-' + this._safe(tab.id));
				p.hidden = key(tab.id) !== this._active;
				this._body.appendChild(p);
			}
		}
		if (focused) { const t = this._tabEl(focused); if (t) t.focus({ preventScroll: true }); }
		this._reveal();
	}

	_safe(id) { return key(id).replace(/[^A-Za-z0-9_-]/g, '_'); }

	_buildTab(tab) {
		const active = key(tab.id) === this._active;
		const closable = tab.closable !== undefined ? !!tab.closable : !!this.options.closable;
		const el = h('div', {
			className: 'dfc-tabbar__tab' + (active ? ' is-active' : '') + (tab.disabled ? ' is-disabled' : '') + (closable ? ' is-closable' : '') + (tab.css ? ' ' + tab.css : ''),
			role: 'tab',
			id: this.id + '-tab-' + this._safe(tab.id),
			'aria-selected': active ? 'true' : 'false',
			'aria-controls': this.options.noContent ? null : this.id + '-panel-' + this._safe(tab.id),
			'aria-disabled': tab.disabled ? 'true' : null,
			tabIndex: active ? 0 : -1,
			title: tab.tooltip || null,
		});
		el.dataset.id = key(tab.id);
		if (tab.hidden) el.hidden = true;
		const icon = renderIcon(tab.icon, 'dfc-tabbar__icon');
		if (icon) el.appendChild(icon);
		if (tab.label !== undefined && tab.label !== null && tab.label !== '') el.appendChild(h('span', { className: 'dfc-tabbar__label', text: String(tab.label) }));
		else el.setAttribute('aria-label', tab.tooltip || key(tab.id));
		const count = Number(tab.count);
		if (count > 0) el.appendChild(h('span', { className: 'dfc-tabbar__count dfc-tabbar__count--' + (tab.countColor || 'neutral'), text: count > 99 ? '99+' : String(count) }));
		if (closable) {
			const x = h('span', { className: 'dfc-tabbar__close', 'aria-hidden': 'true', title: 'Close' }, renderIcon('xmark'));
			el.appendChild(x);
		}
		return el;
	}

	_tabEl(id) { return this._list.querySelector('[data-id="' + (window.CSS && CSS.escape ? CSS.escape(key(id)) : key(id)) + '"]'); }

	/** Scrolls the active tab into view when the header overflows. */
	_reveal() {
		const t = this._active ? this._tabEl(this._active) : null;
		if (t && t.scrollIntoView && this._header.scrollWidth > this._header.clientWidth) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
	}

	// ── Events ─────────────────────────────────────────────────────────────

	/** Tells the payload listeners about every event on the positional channel. */
	_relay(name, args) {
		if (name === 'beforechange') { const p = this._payload({ id: args[0], previous: args[1] }); this.emit('beforeChange', p); return !p.defaultPrevented; }
		if (name === 'change') { this.emit('change', { id: args[0], previous: args[1], value: args[0], source: this._source || 'api' }); return; }
		if (name === 'beforeclose') { const p = this._payload({ id: args[0] }); this.emit('beforeClose', p); return !p.defaultPrevented; }
		if (name === 'close') { this.emit('close', { id: args[0] }); return; }
		this.emit(name, { args });
	}

	_payload(extra) {
		const p = Object.assign({ defaultPrevented: false }, extra);
		p.preventDefault = function () { p.defaultPrevented = true; };
		return p;
	}

	bind() {
		this._onClick = (ev) => {
			const tabEl = ev.target.closest ? ev.target.closest('.dfc-tabbar__tab') : null;
			if (!tabEl || !this._list.contains(tabEl)) return;
			const id = tabEl.dataset.id;
			const tab = this._byId.get(id);
			if (!tab || tab.disabled) return;
			if (ev.target.closest('.dfc-tabbar__close')) { this._userClose(id); return; }
			this._select(id, 'user');
			const t = this._tabEl(id);
			if (t) t.focus({ preventScroll: true });
		};
		this._onKey = (ev) => {
			const tabEl = ev.target.closest ? ev.target.closest('.dfc-tabbar__tab') : null;
			if (!tabEl || !this._list.contains(tabEl)) return;
			const visible = Array.prototype.filter.call(this._list.children, (t) => !t.hidden);
			const at = visible.indexOf(tabEl);
			const next = this._vertical ? 'ArrowDown' : 'ArrowRight';
			const prev = this._vertical ? 'ArrowUp' : 'ArrowLeft';
			const move = (i) => {
				const n = visible.length; if (!n) return;
				const t = visible[(i + n) % n];
				visible.forEach((v) => { v.tabIndex = v === t ? 0 : -1; });
				t.focus();
				if (this.options.activation !== 'manual' && t.getAttribute('aria-disabled') !== 'true') this._select(t.dataset.id, 'user');
			};
			switch (ev.key) {
				case next: ev.preventDefault(); move(at + 1); break;
				case prev: ev.preventDefault(); move(at - 1); break;
				case 'Home': ev.preventDefault(); move(0); break;
				case 'End': ev.preventDefault(); move(visible.length - 1); break;
				case 'Enter': case ' ':
					ev.preventDefault();
					if (tabEl.getAttribute('aria-disabled') !== 'true') this._select(tabEl.dataset.id, 'user');
					break;
				case 'Delete':
					if (tabEl.classList.contains('is-closable') && tabEl.getAttribute('aria-disabled') !== 'true') { ev.preventDefault(); this._userClose(tabEl.dataset.id); }
					break;
				default: break;
			}
		};
		this.el.addEventListener('click', this._onClick);
		this.el.addEventListener('keydown', this._onKey);
	}

	_userClose(id) {
		const visible = this._tabs.filter((t) => !t.hidden);
		const at = visible.findIndex((t) => key(t.id) === key(id));
		if (!this.events.fire('beforeClose', [id])) return;
		const neighbour = visible[at + 1] || visible[at - 1];
		this.remove(id, 'user');
		this.events.fire('close', [id]);
		const t = neighbour ? this._tabEl(neighbour.id) : null;
		if (t) t.focus({ preventScroll: true });
	}

	/** Activates a tab, asking beforeChange first. @returns {boolean} whether it changed. */
	_select(id, source) {
		id = key(id);
		const tab = this._byId.get(id);
		if (!tab || id === this._active) return false;
		const previous = this._active;
		const prevTab = previous === null ? null : this._byId.get(previous);
		const prevId = prevTab ? prevTab.id : undefined;
		if (!this.events.fire('beforeChange', [tab.id, prevId])) {
			// Vetoed: the roving tab stop goes back to the tab that stays open.
			const t = previous === null ? null : this._tabEl(previous);
			if (t) this._list.querySelectorAll('.dfc-tabbar__tab').forEach((x) => { x.tabIndex = x === t ? 0 : -1; });
			return false;
		}
		this._active = id;
		this._paintActive();
		if (this._silentValue) return true;
		this._source = source;
		this.events.fire('change', [tab.id, prevId]);
		this._source = null;
		this.emit('valueChanged', { value: tab.id, previous: prevId, source: source || 'api' });
		return true;
	}

	_paintActive() {
		this._list.querySelectorAll('.dfc-tabbar__tab').forEach((t) => {
			const on = t.dataset.id === this._active;
			t.classList.toggle('is-active', on);
			t.setAttribute('aria-selected', on ? 'true' : 'false');
			t.tabIndex = on ? 0 : -1;
		});
		this._panels.forEach((p, id) => { p.hidden = id !== this._active; });
		this._reveal();
	}

	// ── Public API ─────────────────────────────────────────────────────────

	/** Selects a tab (beforeChange can veto). */
	setActive(id) { this._select(id, 'api'); return this; }
	/** The active tab's id. */
	getActive() { const t = this._active ? this._byId.get(this._active) : null; return t ? t.id : undefined; }

	get value() { return this.getActive(); }
	set value(id) { this._select(id, 'api'); }
	setValue(id, opts = {}) { if (opts && opts.silent) this._silentValue = true; try { this._select(id, 'api'); } finally { this._silentValue = false; } return this; }

	/** The tab's record (live), or undefined. */
	getTab(id) { return this._byId.get(key(id)); }
	/** Every tab, in order (live records). */
	getTabs() { return this._tabs.slice(); }

	/** The content element of a tab (null in noContent mode). */
	getPanel(id) { return this._panels.get(key(id)) || null; }

	/**
	 * Puts content in a tab's panel, replacing what was there: an element, an
	 * HTML string, an object with an `el` element, or an object with a
	 * `mount(element)` method.
	 */
	attach(id, content) {
		const p = this.getPanel(id);
		if (!p) return this;
		p.innerHTML = '';
		if (content === undefined || content === null) return this;
		if (typeof content === 'string') p.innerHTML = content;
		else if (content instanceof Node) p.appendChild(content);
		else if (content.el instanceof Node) p.appendChild(content.el);
		else if (typeof content.mount === 'function') content.mount(p);
		return this;
	}
	/** Same as attach with an HTML string. */
	attachHTML(id, html) { return this.attach(id, String(html === undefined || html === null ? '' : html)); }
	/** Empties a tab's panel. */
	detach(id) { const p = this.getPanel(id); if (p) p.innerHTML = ''; return this; }

	/** A small handle for one tab's panel: { id, el, attach(x), attachHTML(html), detach() }. */
	getCell(id) {
		const self = this;
		const el = this.getPanel(id);
		if (!el) return null;
		return { id, el, attach(x) { self.attach(id, x); return this; }, attachHTML(html) { self.attachHTML(id, html); return this; }, detach() { self.detach(id); return this; } };
	}

	/** Adds a tab at an index (end by default). */
	add(tab, index) {
		const t = this._store(tab, index);
		if (!t) return this;
		if (this._active === null && !t.hidden && !t.disabled) this._active = key(t.id);
		this._renderTabs();
		return this;
	}

	/** Removes a tab and its panel. When it was active, a neighbour takes over. */
	remove(id, source) {
		id = key(id);
		const tab = this._byId.get(id);
		if (!tab) return this;
		const visible = this._tabs.filter((t) => !t.hidden && !t.disabled);
		const at = visible.indexOf(tab);
		this._tabs.splice(this._tabs.indexOf(tab), 1);
		this._byId.delete(id);
		const p = this._panels.get(id);
		if (p) { p.remove(); this._panels.delete(id); }
		if (this._active === id) {
			this._active = null;
			this._renderTabs();
			const next = visible[at + 1] || visible[at - 1];
			if (next) this._select(next.id, source === 'user' ? 'user' : 'api');
		} else this._renderTabs();
		return this;
	}

	/**
	 * With a tab id and a patch, changes that tab (label, icon, count, closable,
	 * disabled, hidden, css, tooltip); with one object, merges options as every
	 * component does.
	 */
	update(idOrOptions, patch) {
		if ((typeof idOrOptions === 'string' || typeof idOrOptions === 'number') && patch && typeof patch === 'object') {
			const tab = this._byId.get(key(idOrOptions));
			if (!tab) return this;
			const rest = Object.assign({}, patch);
			delete rest.id;
			if (rest.tab !== undefined && rest.label === undefined) rest.label = rest.tab;
			Object.assign(tab, rest);
			const old = this._tabEl(tab.id);
			const hadFocus = old && old === document.activeElement;
			if (old) old.replaceWith(this._buildTab(tab)); else this._renderTabs();
			if (hadFocus) { const t = this._tabEl(tab.id); if (t) t.focus({ preventScroll: true }); }
			if ((tab.hidden || tab.disabled) && key(tab.id) === this._active) {
				const next = this._tabs.find((t) => !t.hidden && !t.disabled);
				if (next) this._select(next.id, 'api');
			}
			return this;
		}
		return super.update(idOrOptions || {});
	}

	show(id) { return this.update(id, { hidden: false }); }
	hide(id) { return this.update(id, { hidden: true }); }
	enable(id) { return this.update(id, { disabled: false }); }
	disable(id) { return this.update(id, { disabled: true }); }

	/** Same as destroy(). */
	destructor() { this.destroy(); }

	unbind() {
		if (this._onClick) this.el.removeEventListener('click', this._onClick);
		if (this._onKey) this.el.removeEventListener('keydown', this._onKey);
		if (this.events) this.events.clear();
	}
}

register('tabbar', Tabbar);
export default Tabbar;
